import React, { useState, useCallback } from 'react';
import { FieldValues, useController } from 'react-hook-form';

import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Checkbox, { CheckboxProps } from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Tooltip from '@mui/material/Tooltip';
import TextField, { TextFieldProps } from '@mui/material/TextField';

import InfoIcon from '@mui/icons-material/InfoOutlined';

import { FormField } from '../types';

export type FormConditionalTextFieldProps<TField extends FieldValues> = {
  checkboxProps?: CheckboxProps;
  checkboxLabel?: React.ReactNode;
  tooltip?: React.ReactNode;

  direction?: 'row' | 'row-reverse' | 'column' | 'column-reverse';

  deactivatedValue?: string | number | null;
} & FormField<TField> &
  Omit<TextFieldProps, 'required'>;

const FormConditionalTextField = <TField extends FieldValues>(props: FormConditionalTextFieldProps<TField>) => {
  const {
    sx,
    name,
    control,
    defaultFormValue = '',
    shouldUnregister,
    rules,
    checkboxLabel,
    checkboxProps = {},
    tooltip,
    variant = 'standard',
    size = 'small',
    deactivatedValue = '',
    direction = 'column',
    ...textFieldProps
  } = props;

  const { field, fieldState } = useController({
    control,
    name,
    defaultValue: defaultFormValue,
    shouldUnregister,
    rules: {
      required: rules?.required,
    },
  });
  const { value, onChange } = field;
  const { error } = fieldState;
  const hasError = !!error;

  const [enabled, setEnabled] = useState(!!value);

  const handleToggleCheckbox = useCallback(() => {
    setEnabled(!enabled);
    if (enabled && !!value) {
      onChange(deactivatedValue);
    }
  }, [onChange, value, enabled, deactivatedValue]);

  return (
    <Stack sx={sx} direction={direction} spacing={1}>
      <Box position="relative">
        <FormControlLabel control={<Checkbox checked={enabled} onChange={handleToggleCheckbox} {...checkboxProps} />} label={checkboxLabel} />
        {tooltip && (
          <Tooltip title={tooltip} placement="bottom-end" sx={{ position: 'absolute', top: -4, right: -4 }}>
            <InfoIcon fontSize="small" />
          </Tooltip>
        )}
      </Box>
      {enabled && (
        <TextField
          {...textFieldProps}
          variant={variant}
          size={size}
          error={!!hasError}
          required={!!rules?.required}
          helperText={error?.message || undefined}
          {...field}
        />
      )}
    </Stack>
  );
};

export default FormConditionalTextField;
